import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'

import type { PatchScheduleTemplateBody, PostScheduleTemplateBody } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/**
 * Invalidates the template list, the clinic schedule (block edits change
 * availability), and the overrides list (its rows display the joined
 * template name, which a rename/edit would otherwise leave stale). A template's
 * blocks feed booking availability, so bust the slot queries too — otherwise the
 * calendar keeps offering stale bookable/unbookable slots until a reload (same
 * reasoning as useSaveStaffRotation).
 */
function invalidateTemplates(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['schedule-templates'] })
  qc.invalidateQueries({ queryKey: ['clinic-schedule'] })
  qc.invalidateQueries({ queryKey: ['admin', 'schedule-overrides'] })
  qc.invalidateQueries({ queryKey: ['available-slots-range'] })
  qc.invalidateQueries({ queryKey: ['available-slots'] })
}

/** Creates a schedule template. */
export function useCreateScheduleTemplate() {
  const api = useOsApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: PostScheduleTemplateBody) => {
      const res = await api.post('/schedules/templates', { body })
      if (!res.success) throw new Error(res.error.message)
    },
    onSuccess: () => invalidateTemplates(qc),
  })
}

/** Updates an existing schedule template's name and/or blocks. */
export function useUpdateScheduleTemplate() {
  const api = useOsApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PatchScheduleTemplateBody }) => {
      const res = await api.patch('/schedules/templates/:id', { params: { id }, body })
      if (!res.success) throw new Error(res.error.message)
    },
    onSuccess: () => invalidateTemplates(qc),
  })
}

/** Archives or unarchives a schedule template. Unarchiving fails with a 409 if an active template has taken its name. */
export function useSetScheduleTemplateArchived() {
  const api = useOsApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const res = await api.patch('/schedules/templates/:id', {
        params: { id },
        body: { archived },
      })
      if (!res.success) throw new Error(res.error.message)
    },
    onSuccess: () => invalidateTemplates(qc),
  })
}
